// src/pages/session/SessionDetailsPage.js - Organized layout
import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Chip,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BoltIcon from '@mui/icons-material/Bolt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeIcon from '@mui/icons-material/Badge';
import EvStationIcon from '@mui/icons-material/EvStation';
import PowerIcon from '@mui/icons-material/Power';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TimelineIcon from '@mui/icons-material/Timeline';
import { getSession, getSessionStats, getSessionMeterValues } from '../../api/sessionAPI';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import SessionPaymentStatus from '../../components/session/SessionPaymentStatus';

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString('en-GB');
  } catch {
    return dateString;
  }
};

const SessionDetailsPage = () => {
  const { id } = useParams();
  const sessionId = parseInt(id);

  // Fetch session details
  const { data: session, isLoading, isError, error } = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => getSession(sessionId),
    enabled: !!sessionId && !isNaN(sessionId)
  });

  // Fetch session statistics
  const { data: stats } = useQuery({
    queryKey: ['session-stats', sessionId],
    queryFn: () => getSessionStats(sessionId),
    enabled: !!sessionId && !isNaN(sessionId)
  });

  // Fetch meter values for chart
  const { data: meterValues } = useQuery({
    queryKey: ['session-meter', sessionId],
    queryFn: () => getSessionMeterValues(sessionId),
    enabled: !!sessionId && !isNaN(sessionId)
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError || !session) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading session'} />;
  }

  // Prepare chart data - optimized for the exact format returned by the backend
  const chartData = meterValues?.map(reading => ({
    time: new Date(reading.timestamp).toLocaleTimeString('en-GB'),
    energy: reading.energy_kwh, // Backend already provides energy_kwh
  })) || [];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4">Charging Session # {sessionId}</Typography>
        </div>
        
        <Button 
          component={RouterLink} 
          to="/sessions" 
          startIcon={<ArrowBackIcon />}
        >
          Back to Sessions
        </Button>
      </Box>

      {/* Main Session Information */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Session Information</Typography>
        
        {/* First Row - Basic Info */}
        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} sm={6} md={3}>
            <List dense disablePadding>
              <ListItem>
                <ListItemIcon>
                  <EvStationIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Charger" 
                  secondary={`ID: ${session.ChargerSessionChargerId} | Connector: ${session.ChargerSessionConnectorId}`} 
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <List dense disablePadding>
              <ListItem>
                <ListItemIcon>
                  <BadgeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Driver Info" 
                  secondary={session.ChargerSessionDriverId ? 
                    `Driver ID: ${session.ChargerSessionDriverId} | RFID: ${session.ChargerSessionRFIDCard || 'N/A'}` : 
                    'No driver information'} 
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <List dense disablePadding>
              <ListItem>
                <ListItemText 
                  primary="Status" 
                  secondary={
                    <Chip 
                      label={session.ChargerSessionStatus || 'Unknown'} 
                      color={session.ChargerSessionStatus === 'Completed' ? 'success' : 'primary'}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  } 
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <List dense disablePadding>
              {session.ChargerSessionReason ? (
                <ListItem>
                  <ListItemText 
                    primary="Stop Reason" 
                    secondary={session.ChargerSessionReason} 
                  />
                </ListItem>
              ) : (
                <ListItem>
                  <ListItemText 
                    primary="Stop Reason" 
                    secondary="-" 
                  />
                </ListItem>
              )}
            </List>
          </Grid>
        </Grid>
        
        {/* Second Row - Time and Energy */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <List dense disablePadding>
              <ListItem>
                <ListItemIcon>
                  <AccessTimeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Start Time" 
                  secondary={formatDateTime(session.ChargerSessionStart)} 
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <List dense disablePadding>
              <ListItem>
                <ListItemIcon>
                  <AccessTimeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="End Time" 
                  secondary={session.ChargerSessionEnd ? formatDateTime(session.ChargerSessionEnd) : 'Ongoing'} 
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <List dense disablePadding>
              <ListItem>
                <ListItemText 
                  primary="Duration" 
                  secondary={stats?.duration_formatted || formatDuration(session.ChargerSessionDuration)} 
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <List dense disablePadding>
              <ListItem>
                <ListItemIcon>
                  <BoltIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Total Energy" 
                  secondary={`${stats?.energy_kwh?.toFixed(2) || session.ChargerSessionEnergyKWH?.toFixed(2) || '0'} kWh`} 
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* Payment Information */}
      <Grid container spacing={3} mb={3}>
        {/* <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, h: '100%' }}>
            <Typography variant="h6" gutterBottom>Additional Power Details</Typography>
            <List dense disablePadding>
              {stats?.max_power_kw > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <PowerIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Maximum Power" 
                    secondary={`${stats.max_power_kw.toFixed(2)} kW`} 
                  />
                </ListItem>
              )}
              
              {stats?.avg_power_kw > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <PowerIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Average Power" 
                    secondary={`${stats.avg_power_kw.toFixed(2)} kW`} 
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid> */}

        <Grid item xs={12} md={12}>
          <Paper elevation={2} sx={{ p: 3, h: '100%' }}>
            <Typography variant="h6" gutterBottom>Payment & Cost</Typography>
            
            {/* Payment Status Component */}
            <Box mb={2}>
              <SessionPaymentStatus session={session} />
            </Box>
            
            {/* Cost Information
            {(session.ChargerSessionCost || session.ChargerSessionPaymentAmount) && (
              <List dense disablePadding>
                {session.ChargerSessionCost && (
                  <ListItem>
                    <ListItemIcon>
                      <AttachMoneyIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Session Cost" 
                      secondary={`${session.ChargerSessionCost.toFixed(2)}`} 
                    />
                  </ListItem>
                )}
                
                {session.ChargerSessionPaymentAmount && (
                  <ListItem>
                    <ListItemIcon>
                      <AttachMoneyIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Payment Amount" 
                      secondary={`${session.ChargerSessionPaymentAmount.toFixed(2)}`} 
                    />
                  </ListItem>
                )}
                
                {session.ChargerSessionPaymentStatus && (
                  <ListItem>
                    <ListItemText 
                      primary="Payment Status" 
                      secondary={session.ChargerSessionPaymentStatus} 
                    />
                  </ListItem>
                )}
              </List>
            )} */}
          </Paper>
        </Grid>
      </Grid>

      {/* Energy Chart (only if there's data) */}
      {chartData.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <TimelineIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Session Energy Timeline</Typography>
          </Box>
          
          <Box sx={{ width: '100%', height: 400 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis domain={[0, 'auto']} />
                <Tooltip 
                  formatter={(value) => [`${value.toFixed(3)} kWh`, 'Energy']}
                  labelFormatter={(time) => `Time: ${time}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="energy" 
                  stroke="#16a34a" 
                  name="Energy (kWh)" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6, fill: '#16a34a', stroke: '#fff', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default SessionDetailsPage;